import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { useLanguage } from '../../../context/LanguageContext';
import api from '../../../services/api';
import { ReturnsTable } from '../../Returns';

function todayMinus(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function DeductionsReport({ storeId }) {
  const { t } = useLanguage();
  const [from, setFrom] = useState(todayMinus(1));
  const [to, setTo] = useState(todayMinus(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.chatUiReportsDeductions({
        date_from: from,
        date_to: to,
        store_id: storeId || undefined,
      });
      setData(res?.data ?? res);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const w = data?.returns_count_24_48_72 || {};

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
        <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ maxWidth: 160 }} />
        <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ maxWidth: 160 }} />
        <Button size="sm" className="btn-gradient" onClick={load} disabled={loading}>
          {loading ? <Spinner size="sm" /> : t('chatApp.load')}
        </Button>
      </div>
      {data && (
        <>
          <p className="small text-muted">
            {t('chatApp.returnsWindows')}: {w['24'] ?? 0} / {w['48'] ?? 0} / {w['72'] ?? 0}
            {' · '}¥{data.total_charges}
          </p>
          {(data.by_type || []).map((row) => (
            <details key={row.type} className="chat-report-card" open={row.count > 0}>
              <summary>
                {row.label}: {row.count} · ¥{row.total}
              </summary>
              {(row.barcodes || []).length > 0 && (
                <Table size="sm" className="mt-2 mb-0">
                  <thead>
                    <tr>
                      <th>{t('chatApp.barcode')}</th>
                      <th>#</th>
                      <th>¥</th>
                    </tr>
                  </thead>
                  <tbody>
                    {row.barcodes.map((b) => (
                      <tr key={`${row.type}-${b.barcode}`}>
                        <td>{b.barcode}</td>
                        <td>{b.count}</td>
                        <td>{b.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </details>
          ))}
        </>
      )}
    </div>
  );
}

export function TopupsReport({ storeId }) {
  const { t } = useLanguage();
  const [from, setFrom] = useState(todayMinus(30));
  const [to, setTo] = useState(todayMinus(0));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState('0');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.chatUiReportsTopups({
        date_from: from,
        date_to: to,
        store_id: storeId || undefined,
      });
      const d = res?.data ?? res;
      setRows(d.rows || []);
      setTotal(d.total || '0');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ maxWidth: 160 }} />
        <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ maxWidth: 160 }} />
        <Button size="sm" className="btn-gradient" onClick={load} disabled={loading}>
          {t('chatApp.load')}
        </Button>
      </div>
      <p className="fw-semibold">¥{total}</p>
      <Table size="sm" striped bordered>
        <thead>
          <tr>
            <th>{t('chatApp.date')}</th>
            <th>{t('chatApp.store')}</th>
            <th>¥</th>
            <th>{t('chatApp.comment')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.date}</td>
              <td>{r.store_external_id}</td>
              <td>{r.amount}</td>
              <td>{r.comment}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export function SalesReport({ selectedStoreId }) {
  return (
    <div className="chat-sales-embed">
      <ReturnsTable activeTab="whSale" hideTabNavigation selectedStoreId={selectedStoreId} />
    </div>
  );
}

export function ReturnsSummaryReport({ storeId }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.chatUiReportsReturns({ store_id: storeId || undefined }).then((res) => {
      setData(res?.data ?? res);
    });
  }, [storeId]);

  if (!data) return <Spinner size="sm" />;
  return (
    <div className="admin-reports-pulse-grid">
      <div className="chat-report-card">
        <div className="fw-bold fs-4 text-primary">{data.wb_received_completed}</div>
        <div className="small text-muted">{t('chatApp.wbReceived')}</div>
      </div>
      <div className="chat-report-card">
        <div className="fw-bold fs-4 text-primary">{data.our_receipts}</div>
        <div className="small text-muted">{t('chatApp.ourReceipts')}</div>
      </div>
      <div className="chat-report-card">
        <div className="fw-bold fs-4 text-danger">{data.debt_count}</div>
        <div className="small text-muted">{t('chatApp.debt')}</div>
      </div>
    </div>
  );
}
